<template>
  <div class="bg-white overflow-hidden relative flex flex-col">
    <main class="flex-1 overflow-y-auto hide-scrollbar p-5 space-y-5 pb-tabbar-safe">
      <section class="animate-slide-up space-y-4 mb-1" style="animation-delay: 0.1s">
        <div class="flex justify-between items-center gap-3">
          <h2 class="text-gray-400 text-lg font-bold uppercase tracking-wider">
            {{ fiscalYearLabel }} NSP TOTAL
          </h2>
          <div class="w-44 shrink-0 relative">
            <van-slider
              v-model="sliderRange"
              :min="0"
              :max="3"
              :step="1"
              range
              :bar-height="7"
              :button-size="16"
              active-color="#000000"
              inactive-color="#E5E7EB"
              class="relative z-10"
              @drag-start="onSliderStart"
              @drag-end="onSliderEnd"
            >
              <template #left-button>
                <div class="w-4 h-4 bg-white border-2 border-black rounded-[9999px] shadow"></div>
              </template>
              <template #right-button>
                <div class="w-4 h-4 bg-white border-2 border-black rounded-[9999px] shadow"></div>
              </template>
            </van-slider>
            <div class="absolute left-0 right-0 top-full mt-1 flex justify-between text-xs text-gray-500 tracking-wide">
              <span>Q1</span>
              <span>Q2</span>
              <span>Q3</span>
              <span>Q4</span>
            </div>
          </div>
        </div>
        <p class="text-center text-2xl font-black">
          <span class="text-black">AOP :</span>
          <span :style="{ color: NSP_COLORS.total }" class="pl-1">{{ nspBreakdown.total }}</span>
          <span class="text-gray-400">(</span>
          <span :style="{ color: NSP_COLORS.n6n9k3k9 }">{{ nspBreakdown.n6 }}</span>
          <span class="text-gray-400">+</span>
          <span :style="{ color: NSP_COLORS.n101K10 }">{{ nspBreakdown.n101 }}</span>
          <span class="text-gray-400">+</span>
          <span :style="{ color: NSP_COLORS.dnc }">{{ nspBreakdown.dnc }}</span>
          <span class="text-gray-400">+</span>
          <span :style="{ color: NSP_COLORS.opened }">{{ nspBreakdown.opened }}</span>
          <span class="text-gray-400">)/</span>
          <span class="text-black">{{ nspBreakdown.aop }}</span>
        </p>
        <div class="flex items-center gap-3">
          <div class="relative h-48 flex-1">
            <div class="absolute inset-0" ref="chartEl"></div>
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div class="text-center">
                <p class="text-3xl font-black leading-none" :style="{ color: NSP_COLORS.total }">
                  {{ nspRateTotal }}%
                </p>
                <p class="text-sm text-gray-400 mt-1">
                  ⏱{{ nspTimeProgress }}%
                </p>
                <p class="text-[10px] font-bold uppercase text-gray-400 mt-0.5">TIME PROGRESS</p>
              </div>
            </div>
          </div>
          <div class="shrink-0 flex flex-col gap-2 pr-1">
            <div
              v-for="lg in ringLegend"
              :key="lg.key"
              class="flex items-center gap-1.5 text-[11px] font-bold uppercase text-gray-500"
            >
              <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: lg.color }"></span>
              {{ lg.label }}
            </div>
          </div>
        </div>
        <p class="text-center text-2xl font-black">
          <span class="text-black">TOTAL :</span>
          <span :style="{ color: NSP_COLORS.total }" class="pl-1">{{ nspSubTotals.total }}</span>
          <span class="text-gray-400">(</span>
          <span :style="{ color: NSP_COLORS.n6n9k3k9 }">{{ nspSubTotals.applying }}</span>
          <span class="text-gray-400">+</span>
          <span :style="{ color: NSP_COLORS.n101K10 }">{{ nspSubTotals.confirmed }}</span>
          <span class="text-gray-400">+</span>
          <span :style="{ color: NSP_COLORS.dnc }">{{ nspSubTotals.dnc }}</span>
          <span class="text-gray-400">+</span>
          <span :style="{ color: NSP_COLORS.opened }">{{ nspSubTotals.opened }}</span>
          <span class="text-gray-400">)</span>
        </p>
        <div>
          <div
            class="grid gap-x-2 pb-1 text-[10px] font-bold uppercase"
            :style="{ gridTemplateColumns: `1.2fr repeat(${nspChildrenTable.columns.length}, 1fr)` }"
          >
            <span></span>
            <span
              v-for="col in nspChildrenTable.columns"
              :key="'h-' + col.key"
              class="text-right whitespace-nowrap"
              :style="{ color: col.color }"
            >{{ col.label }}</span>
          </div>
          <div v-if="nspLoading" class="py-4 text-center text-xs text-gray-400">Loading...</div>
          <div v-else>
            <div
              v-for="row in nspChildrenTable.rows"
              :key="row.key"
              class="grid gap-x-2 items-center py-2 border-b border-gray-100 text-sm"
              :style="{ gridTemplateColumns: `1.2fr repeat(${nspChildrenTable.columns.length}, 1fr)` }"
            >
              <div class="text-[11px] font-bold uppercase text-black">{{ row.label }}</div>
              <div
                v-for="(v, i) in row.values"
                :key="'v-' + row.key + '-' + i"
                class="text-right font-black whitespace-nowrap"
                :style="{ color: nspChildrenTable.columns[i]?.color }"
              >{{ v }}</div>
            </div>
            <div v-if="!nspChildrenTable.rows.length" class="py-4 text-center text-xs text-gray-400">No Data</div>
          </div>
        </div>
      </section>
      <section class="space-y-2 animate-slide-up mb-3" style="animation-delay: 0.2s">
        <h3 class="text-black text-sm font-bold uppercase tracking-wider">Progress Overview</h3>
        <div
          v-for="card in progressOverviewCards"
          :key="card.name"
          class="bg-white rounded-2xl p-3 border border-gray-100 shadow-sm space-y-1"
        >
          <div class="leading-none">
            <span class="text-xs font-bold uppercase text-gray-500">{{ card.name }} DOOR</span>
          </div>
          <div
            class="grid items-center"
            :style="{ gridTemplateColumns: 'auto repeat(5, 1fr)' }"
          >
            <span></span>
            <span class="text-center text-[10px] font-bold uppercase" :style="{ color: NSP_COLORS.n6n9k3k9 }">APPLYING</span>
            <span class="text-center text-[10px] font-bold uppercase" :style="{ color: NSP_COLORS.n101K10 }">CONFIRMED</span>
            <span class="text-center text-[10px] font-bold uppercase" :style="{ color: NSP_COLORS.dnc }">D&amp;C</span>
            <span class="text-center text-[10px] font-bold uppercase" :style="{ color: NSP_COLORS.opened }">OPENED</span>
            <span class="text-center text-[10px] font-bold uppercase" :style="{ color: NSP_COLORS.total }">TOTAL</span>

            <div class="col-span-full border-t border-gray-100 my-0.5"></div>

            <div class="flex items-center pl-1">
              <span class="inline-block w-9 text-center py-0.5 rounded text-[10px] font-bold bg-black text-white">168#</span>
            </div>
            <div
              v-for="m in card.metrics"
              :key="'p-' + m.key"
              class="min-h-[38px] py-1 flex flex-col items-center justify-center"
            >
              <p class="text-sm font-black leading-tight" :style="{ color: m.color }">{{ m.value }}</p>
              <p v-if="card.aop > 0" class="text-[10px] leading-tight" :style="{ color: m.color, opacity: 0.7 }">{{ round1(m.rate) }}%</p>
            </div>
            <div class="min-h-[38px] py-1 flex flex-col items-center justify-center">
              <p class="text-sm font-black leading-tight" :style="{ color: NSP_COLORS.total }">{{ card.totalValue }}</p>
              <p v-if="card.aop > 0" class="text-[10px] leading-tight" :style="{ color: NSP_COLORS.total, opacity: 0.7 }">{{ card.totalRate }}%</p>
            </div>

            <div class="col-span-full border-t border-gray-100 mt-0.5"></div>

            <div class="self-stretch flex items-center py-1 pl-1 bg-[#FAFAFA] min-h-[38px]">
              <span class="inline-block w-9 text-center py-0.5 rounded text-[10px] font-bold bg-black text-white">TTL</span>
            </div>
            <div
              v-for="m in card.othersMetrics"
              :key="'t-' + m.key"
              class="self-stretch flex items-center justify-center py-1 bg-[#FAFAFA] min-h-[38px]"
            >
              <p class="text-sm font-black leading-tight" :style="{ color: m.color }">{{ m.value }}</p>
            </div>
            <div class="self-stretch flex items-center justify-center py-1 bg-[#FAFAFA] min-h-[38px]">
              <p class="text-sm font-black leading-tight" :style="{ color: NSP_COLORS.total }">{{ card.othersTotal }}</p>
            </div>

            <div class="col-span-full border-t border-gray-100 mb-0.5"></div>
          </div>
        </div>
      </section>
      <h3 class="text-black text-sm font-bold uppercase tracking-wider mb-2">Account Breakdown</h3>
      <section class="px-3 mb-4 animate-slide-up" style="animation-delay: 0.6s">
        <div class="grid grid-cols-[2fr_1fr_1.5fr_1fr] gap-x-2 text-[10px] font-bold uppercase text-gray-400">
          <span>Account</span>
          <span class="text-right whitespace-nowrap">Opened</span>
          <span class="text-right whitespace-nowrap">N6/K3 In Progress</span>
          <span class="text-right whitespace-nowrap">Total</span>
        </div>
        <div v-if="fourPartsLoading" class="py-4 text-center text-xs text-gray-400">Loading...</div>
        <div v-else>
          <div
            v-for="row in visibleRows"
            :key="row.id"
            class="grid grid-cols-[2fr_1fr_1.5fr_1fr] gap-x-2 items-center py-2 border-b border-gray-100 text-sm"
            :style="{ paddingLeft: row.level ? row.level * 12 + 'px' : undefined }"
            @click="row.hasChildren && toggleRow(row)"
          >
            <div class="flex items-center gap-1">
              <van-icon
                v-if="row.hasChildren"
                :name="expandedIds.has(row.id) ? 'arrow-down' : 'arrow'"
                size="12"
                class="text-gray-500"
              />
              <span :class="[ row.level === 0 ? 'text-black font-bold' : 'text-gray-700' ]">{{ row.group }}</span>
            </div>
            <div class="text-right text-green-600 font-black whitespace-nowrap">{{ row.openedNum }}</div>
            <div class="text-right text-blue-500 font-black whitespace-nowrap">{{ row.inProgress }}</div>
            <div class="text-right text-orange-500 font-black whitespace-nowrap">{{ row.total }}</div>
          </div>
          <div v-if="!visibleRows.length" class="py-4 text-center text-xs text-gray-400">No Data</div>
        </div>
      </section>
      <h3 class="text-black text-sm font-bold uppercase tracking-wider mb-2">Gate Approval Overview</h3>
      <section class="px-3 mb-4 animate-slide-up" style="animation-delay: 0.4s">
        <div class="grid grid-cols-[2fr_1fr_1fr_1fr] gap-x-2 text-[10px] font-bold uppercase text-gray-400">
          <span>Gate</span>
          <span class="text-right whitespace-nowrap">On Track</span>
          <span class="text-right whitespace-nowrap">Overdue</span>
          <span class="text-right whitespace-nowrap">Total</span>
        </div>
        <div v-if="fullGateLoading" class="py-4 text-center text-xs text-gray-400">Loading...</div>
        <div v-else>
          <div
            v-for="(g, i) in fullGateRows"
            :key="i"
            class="grid grid-cols-[2fr_1fr_1fr_1fr] gap-x-2 items-center py-2 border-b border-gray-100 text-sm"
          >
            <div class="text-black">{{ g.name }}</div>
            <div class="text-right text-blue-500 font-black whitespace-nowrap">{{ g.onTrack }}</div>
            <div class="text-right text-red-500 font-black whitespace-nowrap">{{ g.overDue }}</div>
            <div class="text-right text-orange-500 font-black whitespace-nowrap">{{ g.total }}</div>
          </div>
          <div v-if="!fullGateRows.length" class="py-4 text-center text-xs text-gray-400">No Data</div>
        </div>
      </section>
      <h3 class="text-black text-sm font-bold uppercase tracking-wider mb-2">Concept Breakdown</h3>
      <section class="px-3 mb-4 animate-slide-up" style="animation-delay: 0.6s">
        <div class="grid grid-cols-[2fr_1fr_1.5fr_1fr] gap-x-2 text-[10px] font-bold uppercase text-gray-400">
          <span>Concept</span>
          <span class="text-right whitespace-nowrap">Opened</span>
          <span class="text-right whitespace-nowrap">N6/K3 In Progress</span>
          <span class="text-right whitespace-nowrap">Total</span>
        </div>
        <div v-if="conceptLoading" class="py-4 text-center text-xs text-gray-400">Loading...</div>
        <div v-else>
          <div
            v-for="row in conceptVisibleRows"
            :key="row.id"
            class="grid grid-cols-[2fr_1fr_1.5fr_1fr] gap-x-2 items-center py-2 border-b border-gray-100 text-sm"
            :style="{ paddingLeft: row.level ? row.level * 12 + 'px' : undefined }"
            @click="row.hasChildren && toggleConceptRow(row)"
          >
            <div class="flex items-center gap-1">
              <van-icon
                v-if="row.hasChildren"
                :name="conceptExpandedIds.has(row.id) ? 'arrow-down' : 'arrow'"
                size="12"
                class="text-gray-500"
              />
              <span :class="[ row.level === 0 ? 'text-black font-bold' : 'text-gray-700' ]">{{ row.group }}</span>
            </div>
            <div class="text-right text-green-600 font-black whitespace-nowrap">{{ row.openedNum }}</div>
            <div class="text-right text-blue-500 font-black whitespace-nowrap">{{ row.inProgress }}</div>
            <div class="text-right text-orange-500 font-black whitespace-nowrap">{{ row.total }}</div>
          </div>
          <div v-if="!conceptVisibleRows.length" class="py-4 text-center text-xs text-gray-400">No Data</div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch, nextTick, computed } from 'vue'
import * as echarts from 'echarts'
import {
  getNspInProgressFourParts,
  getNspInProgressFullGate,
  getNspInProgressNspBy168Tag,
  getTotal,
  type NspBy168TagItem,
} from '@/api/dashboard'
import { permitAccount } from '@/api/login'

const NSP_COLORS = {
  total: '#fa5622',
  n6n9k3k9: '#f5a623',
  n101K10: '#0d9488',
  dnc: '#3b82c4',
  opened: '#00a651',
  timeProgress: '#BFC4C5',
  track: '#E5E7EB',
} as const

const chartEl = ref<HTMLDivElement | null>(null)
let chart: any = null
let resizeHandler: any = null

const quarters = ['Q1', 'Q2', 'Q3', 'Q4']
const selectedQuarters = ref<string[]>([])
const sliderRange = ref<[number, number]>([0, 0])
const isDragging = ref(false)

/** 获取年份，若当前月份在 6 月之前，则返回当前年份，否则返回下一年 */
function getNextYearIfAfterJune() {
  const now = new Date()
  const currentMonth = now.getMonth() + 1
  return currentMonth < 6 ? now.getFullYear() : now.getFullYear() + 1
}

const debounce = <T extends (...args: any[]) => any>(fn: T, delay = 300) => {
  let timer: any
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
}

const rangeOfQuarter = (q: string, baseYear: number) => {
  if (q === 'Q1') return { s: `${baseYear}-06-01`, e: `${baseYear}-08-31` }
  if (q === 'Q2') return { s: `${baseYear}-09-01`, e: `${baseYear}-11-30` }
  if (q === 'Q3') {
    const y = baseYear + 1
    const febLast = ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ? 29 : 28
    return { s: `${baseYear}-12-01`, e: `${y}-02-${febLast}` }
  }
  const y = baseYear + 1
  return { s: `${y}-03-01`, e: `${y}-05-31` }
}

/** 确保选中的季度是连续的 */
const ensureContiguous = (qs: string[]) => {
  if (qs.length === 0) return [] as string[]
  const idx = qs.map((q) => quarters.indexOf(q)).filter((i) => i >= 0)
  const min = Math.min(...idx)
  const max = Math.max(...idx)
  const res: string[] = []
  for (let i = min; i <= max; i++) res.push(quarters[i]!)
  return res
}

const isLocked = (q: string) => {
  if (selectedQuarters.value.length < 2) return false
  const idxs = selectedQuarters.value.map((x) => quarters.indexOf(x))
  const min = Math.min(...idxs)
  const max = Math.max(...idxs)
  const i = quarters.indexOf(q)
  return i > min && i < max
}

const computeRange = (qs: string[]) => {
  const year = getNextYearIfAfterJune()
  if (qs.length === 0) return { yearFront: '', yearEnd: '' }
  const ids = qs.map((q) => quarters.indexOf(q)).filter((i) => i >= 0)
  const min = Math.min(...ids)
  const max = Math.max(...ids)
  const startQ = quarters[min]
  const endQ = quarters[max]
  const start = rangeOfQuarter(startQ!, year - 1).s
  const end = rangeOfQuarter(endQ!, year - 1).e
  return { yearFront: start, yearEnd: end }
}

/**
 * @description 保留一位小数
 * @param num 被除数
 * @param den 除数
 */
const formatPercent = (num?: number, den?: number) => {
  const n = Number(num ?? 0)
  const d = Number(den ?? 0)
  if (!d) return '0'
  const v = (n / d) * 100
  const r = Math.round(v * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

const nspBy168TagData = ref<NspBy168TagItem[]>([])
const nspLoading = ref(false)

const fiscalYearLabel = computed(() => `FY${String(getNextYearIfAfterJune()).slice(-2)}`)
const nspItem = computed(() => nspBy168TagData.value.find((it) => it.name === 'NSP') ?? null)

const nspBreakdown = computed(() => {
  const n = nspItem.value
  if (!n) return { total: 0, n6: 0, n101: 0, dnc: 0, opened: 0, aop: 0 }
  const n6 = n.n6n9k3k9
  const n101 = n.n101K10
  const dnc = n.dnc
  const opened = n.opened
  return { total: n6 + n101 + dnc + opened, n6, n101, dnc, opened, aop: n.aop ?? 0 }
})

const nspRateTotal = computed(() => {
  const n = nspItem.value
  if (!n) return '0'
  const sum =
    (n.n6n9k3k9Rate ?? 0) +
    (n.n101K10Rate ?? 0) +
    (n.n102n104Rate ?? 0) +
    (n.dncRate ?? 0) +
    (n.openedRate ?? 0)
  const r = Math.round(sum * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
})

const nspTimeProgress = computed(() => Number(nspItem.value?.timeProgress ?? 0))

const ringLegend = [
  { key: 'applying', label: 'APPLYING', color: NSP_COLORS.n6n9k3k9 },
  { key: 'confirmed', label: 'CONFIRMED', color: NSP_COLORS.n101K10 },
  { key: 'dnc', label: 'D&C', color: NSP_COLORS.dnc },
  { key: 'opened', label: 'OPENED', color: NSP_COLORS.opened },
]

type ChildTableColumn = {
  key: string
  label: string
  color: string
}
type ChildTableRow = {
  key: string
  label: string
  values: number[]
}

const nspChildrenTable = computed(() => {
  const children = nspItem.value?.children ?? []
  const kids = children.slice(0, 2)
  const metricDefs: Array<{
    key: string
    label: string
    color: string
    getValue: (c: NspBy168TagItem) => number
  }> = [
    { key: 'applying', label: 'APPLYING', color: NSP_COLORS.n6n9k3k9, getValue: (c) => Number(c.n6n9k3k9 ?? 0) },
    {
      key: 'confirmed',
      label: 'CONFIRMED',
      color: NSP_COLORS.n101K10,
      getValue: (c) => Number(c.n101K10 ?? 0) + Number(c.n102n104 ?? 0),
    },
    { key: 'dnc', label: 'D&C', color: NSP_COLORS.dnc, getValue: (c) => Number(c.dnc ?? 0) },
    { key: 'opened', label: 'OPENED', color: NSP_COLORS.opened, getValue: (c) => Number(c.opened ?? 0) },
  ]
  const columns: ChildTableColumn[] = [
    ...metricDefs.map((m) => ({ key: m.key, label: m.label, color: m.color })),
    { key: 'total', label: 'TOTAL', color: NSP_COLORS.total },
  ]
  const kidRows: ChildTableRow[] = kids.map((c, idx) => {
    const metricValues = metricDefs.map((m) => (c ? m.getValue(c) : 0))
    const rowTotal = metricValues.reduce((s, v) => s + v, 0)
    return {
      key: `kid-${idx}`,
      label: String(c?.name ?? ''),
      values: [...metricValues, rowTotal],
    }
  })
  const subTotalValues = columns.map((_, colIdx) =>
    kidRows.reduce((s, r) => s + (r.values[colIdx] ?? 0), 0),
  )
  const subTotalRow: ChildTableRow = {
    key: 'subtotal',
    label: 'SUBTOTAL',
    values: subTotalValues,
  }
  return {
    columns,
    rows: kids.length ? [...kidRows, subTotalRow] : [],
    subTotalValues,
  }
})

const nspSubTotals = computed(() => {
  const [applying = 0, confirmed = 0, dnc = 0, opened = 0, total = 0] =
    nspChildrenTable.value.subTotalValues
  return { applying, confirmed, dnc, opened, total }
})

type OverviewMetric = {
  key: string
  label: string
  color: string
  value: number
  rate: number
}
type OverviewOthersMetric = {
  key: string
  label: string
  color: string
  value: number
}
type OverviewCard = {
  name: string
  badgeClass: string
  aop: number
  metrics: OverviewMetric[]
  totalValue: number
  totalRate: number
  othersMetrics: OverviewOthersMetric[]
  othersTotal: number
}

const OVERVIEW_CARD_DEFS: Array<{ name: string; badgeClass: string }> = [
  { name: 'KEY', badgeClass: 'bg-black text-white' },
  { name: 'NON-KEY', badgeClass: 'bg-black text-white' },
  { name: 'TOP', badgeClass: 'bg-gray-200 text-gray-600' },
]

const round1 = (n: number) => {
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? r : Number(r.toFixed(1))
}

const progressOverviewCards = computed<OverviewCard[]>(() => {
  return OVERVIEW_CARD_DEFS.map((def) => {
    const item = nspBy168TagData.value.find((it) => it.name === def.name)
    const applying = Number(item?.n6n9k3k9 ?? 0)
    const confirmed = Number(item?.n101K10 ?? 0) + Number(item?.n102n104 ?? 0)
    const dnc = Number(item?.dnc ?? 0)
    const opened = Number(item?.opened ?? 0)
    const applyingRate = Number(item?.n6n9k3k9Rate ?? 0)
    const confirmedRate = Number(item?.n101K10Rate ?? 0) + Number(item?.n102n104Rate ?? 0)
    const dncRate = Number(item?.dncRate ?? 0)
    const openedRate = Number(item?.openedRate ?? 0)
    const metrics: OverviewMetric[] = [
      { key: 'applying', label: 'APPLYING', color: NSP_COLORS.n6n9k3k9, value: applying, rate: applyingRate },
      { key: 'confirmed', label: 'CONFIRMED', color: NSP_COLORS.n101K10, value: confirmed, rate: confirmedRate },
      { key: 'dnc', label: 'D&C', color: NSP_COLORS.dnc, value: dnc, rate: dncRate },
      { key: 'opened', label: 'OPENED', color: NSP_COLORS.opened, value: opened, rate: openedRate },
    ]
    const kids = item?.children ?? []
    const sumOf = (fn: (c: NspBy168TagItem) => number) =>
      kids.reduce((s, c) => s + (c ? fn(c) : 0), 0)
    const oApplying = sumOf((c) => Number(c.n6n9k3k9 ?? 0))
    const oConfirmed = sumOf((c) => Number(c.n101K10 ?? 0) + Number(c.n102n104 ?? 0))
    const oDnc = sumOf((c) => Number(c.dnc ?? 0))
    const oOpened = sumOf((c) => Number(c.opened ?? 0))
    const othersMetrics: OverviewOthersMetric[] = [
      { key: 'applying', label: 'APPLYING', color: NSP_COLORS.n6n9k3k9, value: oApplying },
      { key: 'confirmed', label: 'CONFIRMED', color: NSP_COLORS.n101K10, value: oConfirmed },
      { key: 'dnc', label: 'D&C', color: NSP_COLORS.dnc, value: oDnc },
      { key: 'opened', label: 'OPENED', color: NSP_COLORS.opened, value: oOpened },
    ]
    return {
      name: def.name,
      badgeClass: def.badgeClass,
      aop: Number(item?.aop ?? 0),
      metrics,
      totalValue: applying + confirmed + dnc + opened,
      totalRate: round1(applyingRate + confirmedRate + dncRate + openedRate),
      othersMetrics,
      othersTotal: oApplying + oConfirmed + oDnc + oOpened,
    }
  })
})

const pieSeriesBase = {
  type: 'pie' as const,
  startAngle: 90,
  clockwise: true,
  avoidLabelOverlap: false,
  label: { show: false },
  emphasis: { disabled: true },
  itemStyle: { borderWidth: 0 },
}

const getChartOption = () => {
  const n = nspItem.value
  const tp = Number(n?.timeProgress ?? 0)
  const confirmedRate = (n?.n101K10Rate ?? 0) + (n?.n102n104Rate ?? 0)
  const rates = [
    { value: n?.n6n9k3k9Rate ?? 0, color: NSP_COLORS.n6n9k3k9 },
    { value: confirmedRate, color: NSP_COLORS.n101K10 },
    { value: n?.dncRate ?? 0, color: NSP_COLORS.dnc },
    { value: n?.openedRate ?? 0, color: NSP_COLORS.opened },
  ]
  const rateSum = rates.reduce((s, r) => s + r.value, 0)
  return {
    series: [
      {
        ...pieSeriesBase,
        radius: ['78%', '92%'],
        data: [
          { value: tp, itemStyle: { color: NSP_COLORS.timeProgress } },
          { value: Math.max(100 - tp, 0), itemStyle: { color: NSP_COLORS.track } },
        ],
      },
      {
        ...pieSeriesBase,
        radius: ['58%', '72%'],
        data: [
          ...rates.map((r) => ({ value: r.value, itemStyle: { color: r.color } })),
          { value: Math.max(100 - rateSum, 0), itemStyle: { color: NSP_COLORS.track } },
        ],
      },
    ],
  }
}

const renderNspChart = () => {
  if (!chart && chartEl.value) {
    chart = echarts.init(chartEl.value)
  }
  if (!chart) return
  chart.setOption(getChartOption())
}

type FourPartNode = {
  id: string
  group: string
  openedNum: number
  inProgress: number
  children?: FourPartNode[]
}
type DisplayRow = {
  id: string
  level: number
  group: string
  openedNum: number
  inProgress: number
  total: number
  hasChildren: boolean
}

const totalData = ref<any[]>([])
const fourPartsLoading = ref(false)
const fourPartsData = ref<FourPartNode[]>([])
const expandedIds = ref<Set<string>>(new Set())
const conceptLoading = ref(false)
const conceptData = ref<FourPartNode[]>([])
const conceptExpandedIds = ref<Set<string>>(new Set())
const fullGateLoading = ref(false)
const fullGateData = ref<any[]>([])

const normalizeFourParts = (items: any[], path = ''): FourPartNode[] => {
  if (!Array.isArray(items)) return []
  const filtered = items.filter((it: any) => {
    const name = String(it?.group ?? '').trim().toLowerCase()
    return name !== 'total'
  })
  return filtered.map((it: any, idx: number) => {
    const id = path ? `${path}-${idx}` : `${idx}`
    const childrenRaw = it?.children || []
    const node: FourPartNode = {
      id,
      group: String(it?.group ?? ''),
      openedNum: Number(it?.openedNum ?? 0),
      inProgress: Number(it?.inProgress ?? 0),
      children: normalizeFourParts(childrenRaw, id),
    }
    return node
  })
}

const flattenVisible = (nodes: FourPartNode[], expanded: Set<string>, level = 0): DisplayRow[] => {
  const rows: DisplayRow[] = []
  for (const n of nodes) {
    rows.push({
      id: n.id,
      level,
      group: n.group,
      openedNum: n.openedNum,
      inProgress: n.inProgress,
      total: (Number(n.openedNum) || 0) + (Number(n.inProgress) || 0),
      hasChildren: !!(n.children && n.children.length),
    })
    if (n.children && n.children.length && expanded.has(n.id)) {
      rows.push(...flattenVisible(n.children, expanded, level + 1))
    }
  }
  return rows
}

const visibleRows = computed<DisplayRow[]>(() => flattenVisible(fourPartsData.value, expandedIds.value))
const conceptVisibleRows = computed<DisplayRow[]>(() => flattenVisible(conceptData.value, conceptExpandedIds.value))

const toggleRow = (row: DisplayRow) => {
  if (!row.hasChildren) return
  const s = new Set(expandedIds.value)
  if (s.has(row.id)) s.delete(row.id)
  else s.add(row.id)
  expandedIds.value = s
}

const toggleConceptRow = (row: DisplayRow) => {
  if (!row.hasChildren) return
  const s = new Set(conceptExpandedIds.value)
  if (s.has(row.id)) s.delete(row.id)
  else s.add(row.id)
  conceptExpandedIds.value = s
}

const fullGateRows = computed(() =>
  fullGateData.value.map((it: any) => ({
    name: String(it?.name ?? ''),
    onTrack: Number(it?.onTrack ?? 0),
    overDue: Number(it?.overDue ?? 0),
    total: Number(it?.onTrack ?? 0) + Number(it?.overDue ?? 0),
  }))
)

const computeAccountArray = () => {
  const raw = String(sessionStorage.getItem('user_account_group') || '').trim()
  if (!raw) return []
  const lower = raw.toLowerCase()
  return raw.split(',').map((s) => s.trim()).filter(Boolean)
}

const triggerApis = async () => {
  const { yearFront, yearEnd } = computeRange(selectedQuarters.value)
  const year = getNextYearIfAfterJune()
  const payload = {
    doorType: 'All',
    yearFront,
    yearEnd,
    year,
    account: computeAccountArray(),
  }
  try {
    nspLoading.value = true
    fourPartsLoading.value = true
    conceptLoading.value = true
    fullGateLoading.value = true
    const [totalRes, nspRes, fourRes, conceptRes, fullRes] = await Promise.all([
      getTotal(payload),
      getNspInProgressNspBy168Tag(payload),
      getNspInProgressFourParts({ ...payload, type: 'Account' }),
      getNspInProgressFourParts({ ...payload, type: 'Concept' }),
      getNspInProgressFullGate(payload),
    ])
    totalData.value = (Array.isArray(totalRes?.data) ? totalRes?.data : totalRes?.data?.items) || []
    nspBy168TagData.value = Array.isArray(nspRes?.data) ? nspRes.data : []
    renderNspChart()
    const raw = (Array.isArray(fourRes?.data) ? fourRes?.data : fourRes?.data?.items) || []
    fourPartsData.value = normalizeFourParts(raw)
    const conceptRaw = (Array.isArray(conceptRes?.data) ? conceptRes?.data : conceptRes?.data?.items) || []
    conceptData.value = normalizeFourParts(conceptRaw)
    const gateRaw = (Array.isArray(fullRes?.data) ? fullRes?.data : fullRes?.data?.items) || []
    fullGateData.value = gateRaw.map((it: any) => ({
      name: String(it?.name ?? ''),
      onTrack: Number(it?.onTrack ?? 0),
      overDue: Number(it?.overDue ?? 0),
    }))
  } catch {}
  finally {
    nspLoading.value = false
    fourPartsLoading.value = false
    conceptLoading.value = false
    fullGateLoading.value = false
  }
}

const debouncedTrigger = debounce(triggerApis, 400)
watch(
  selectedQuarters,
  (nv) => {
    const contiguous = ensureContiguous(nv)
    if (nv.length !== contiguous.length || nv.some((q, i) => q !== contiguous[i])) {
      selectedQuarters.value = contiguous
      return
    }
    if (contiguous.length) {
      const ids = contiguous.map((q) => quarters.indexOf(q)).filter((i) => i >= 0)
      const min = Math.min(...ids)
      const max = Math.max(...ids)
      const cur = sliderRange.value
      if (cur[0] !== min || cur[1] !== max) sliderRange.value = [min, max]
    } else {
      sliderRange.value = [0, 0]
    }
    if (isDragging.value) return
    debouncedTrigger()
  },
  { deep: true },
)

watch(
  sliderRange,
  (nv) => {
    const [a, b] = nv
    const s = Math.min(a, b)
    const e = Math.max(a, b)
    const next = quarters.slice(s, e + 1)
    const cur = selectedQuarters.value
    const same = next.length === cur.length && next.every((q, i) => q === cur[i])
    if (!same) selectedQuarters.value = next
  },
  { deep: true },
)

const onSliderStart = () => {
  isDragging.value = true
}

const onSliderEnd = () => {
  isDragging.value = false
  const [a, b] = sliderRange.value
  const s = Math.min(a, b)
  const e = Math.max(a, b)
  const next = quarters.slice(s, e + 1)
  const cur = selectedQuarters.value
  const same = next.length === cur.length && next.every((q, i) => q === cur[i])
  if (!same) selectedQuarters.value = next
  debouncedTrigger()
}

onMounted(async () => {
  try {
    const res = await permitAccount()
    if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
      sessionStorage.setItem('user_account_group', res.data.join(','))
    } else {
      sessionStorage.setItem('user_account_group', '')
    }
  } catch {}
  selectedQuarters.value = [...quarters]
  sliderRange.value = [0, 3]
  if (chartEl.value) {
    nextTick(() => {
      chart = echarts.init(chartEl.value)
      resizeHandler = () => chart && chart.resize()
      window.addEventListener('resize', resizeHandler)
    })
  }
})

onBeforeUnmount(() => {
  if (resizeHandler) window.removeEventListener('resize', resizeHandler)
  if (chart) {
    chart.dispose()
    chart = null
  }
})
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
/* 预留底部安全区域，避免被 Tabbar 遮挡 */
.pb-tabbar-safe {
  padding-bottom: calc(env(safe-area-inset-bottom) + var(--van-tabbar-height));
}
@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
.animate-slide-up {
  animation: slideUp 0.6s ease-out forwards;
}
</style>
